import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, ExternalLink, ArrowLeft, ChevronRight, Instagram, Globe, Sparkles } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { DECORATORS, getTypes, sanitizeDecorator, decoratorImage, mapsUrl } from './Decorators';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1200&h=800&fit=crop';

// Same key function Decorators.tsx uses for its managed collection, so the
// slug in the URL always matches the entry the grid card linked from.
export function decoratorSlug(d: any, i: number) {
  return slugify(d.name || String(i));
}

export default function EventManagementDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('decorators', DECORATORS, (d, i) =>
    decoratorSlug(d, i)
  );

  const entry = mc.items.find((d: any, i: number) => decoratorSlug(d, i) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Listing Not Found | Dallas Desi Scoop"
          description="This event management listing could not be found."
          canonical="/event-management"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Listing not found.</p>
        <Link
          to="/event-management"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Event Management
        </Link>
      </div>
    );
  }

  // Apply the same render-time sanitization the category grid uses (blanked
  // fields, forced default images) so the detail page always agrees with the
  // card it was opened from.
  const clean = sanitizeDecorator({ ...entry }) as any;
  const image = decoratorImage(clean);

  const rawAddress = (clean.address || '').trim();
  const hasRealAddress =
    rawAddress !== '' &&
    !['address coming soon', 'coming soon', 'n/a', 'na', '-', 'tbd'].includes(rawAddress.toLowerCase());

  const typeLabel = getTypes(clean.type).join(' · ') || 'Event Management';
  const cityLabel =
    (clean.city || '').trim() ||
    (hasRealAddress ? rawAddress.split(',').slice(-2, -1)[0]?.trim() : '') ||
    'Dallas–Fort Worth';

  const rawPhone = (clean.phone || '').trim();
  const isRealPhone = rawPhone !== '' && rawPhone.toLowerCase() !== 'coming soon' && /\d/.test(rawPhone);
  const hasAnySocial = countSocialLinks(clean) > 0;

  const contactUrl = (clean.contactUrl || '').trim();
  const usableContactUrl = contactUrl && contactUrl !== '#' ? contactUrl : '';
  const isFacebookContact = !!usableContactUrl && usableContactUrl.includes('facebook.com');

  const resolvedMapsUrl = mapsUrl(clean);
  const canonicalPath = `/event-management/${slug}`;

  const title = `${clean.name} | ${typeLabel} in ${cityLabel} | Dallas Desi Scoop`;
  const metaDescription = `${clean.name} is a ${typeLabel.toLowerCase()} provider in ${cityLabel} serving the South Asian community across the Dallas–Fort Worth metroplex. Find contact details, location, and more on Dallas Desi Scoop.`;

  const addressParts = rawAddress.split(',').map((p: string) => p.trim()).filter(Boolean);
  const streetAddress = addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality = addressParts.length >= 2 ? addressParts[addressParts.length - 2] : undefined;

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      name: clean.name,
      image: image || FALLBACK_IMAGE,
      description: `${clean.name}, ${typeLabel} in ${cityLabel}, serving the Dallas–Fort Worth Desi community.`,
      address: hasRealAddress
        ? {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality: addressLocality || undefined,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : undefined,
      telephone: isRealPhone ? rawPhone : undefined,
      url: usableContactUrl || clean.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Event Management', item: `${BASE_URL}/event-management` },
        { '@type': 'ListItem', position: 3, name: clean.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other Event Management listings, same-type first.
  const related = mc.items
    .map((d: any, i: number) => ({ item: d, key: decoratorSlug(d, i) }))
    .filter((r: any) => r.key !== slug)
    .sort(
      (a: any, b: any) =>
        Number(getTypes(b.item.type).some((t: string) => getTypes(clean.type).includes(t))) -
        Number(getTypes(a.item.type).some((t: string) => getTypes(clean.type).includes(t)))
    )
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/event-management"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Event Management
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{clean.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {clean.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <Sparkles className="w-3.5 h-3.5 text-[#51ba4f]" />
              {typeLabel}
            </span>
            {hasRealAddress && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {rawAddress}
              </span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {clean.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {clean.description
              ? `${clean.description} `
              : ''}
            {clean.name} is a {typeLabel.toLowerCase()} provider serving the South Asian community in {cityLabel} and across the Dallas–Fort Worth metroplex.
            {hasRealAddress ? ` You can find them at ${rawAddress}.` : ''}
            {` Contact ${clean.name} directly for availability, pricing, and the full range of event services they offer.`}
          </p>

          {hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={clean} px={20} />
            </div>
          )}
        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {hasRealAddress && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{rawAddress}</span>
              </div>
            )}
            {isRealPhone && (
              <a
                href={`tel:${rawPhone.replace(/[^\d+]/g, '')}`}
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{rawPhone}</span>
              </a>
            )}
            {clean.website && clean.website !== '#' && (
              <a
                href={clean.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>Visit website</span>
              </a>
            )}
          </div>
          <div className="mt-5 flex flex-col gap-2">
            {usableContactUrl && (
              <a
                href={usableContactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity hover:opacity-90"
                style={
                  isFacebookContact
                    ? { backgroundColor: '#1877F2' }
                    : {
                        background:
                          'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)',
                      }
                }
              >
                {isFacebookContact ? <Globe className="w-4 h-4" /> : <Instagram className="w-4 h-4" />}
                {isFacebookContact ? 'Facebook' : 'Instagram'}
              </a>
            )}
            {!usableContactUrl && isRealPhone && (
              <a
                href={`tel:${rawPhone.replace(/[^\d+]/g, '')}`}
                className="flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            {resolvedMapsUrl && (
              <a
                href={resolvedMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Get Directions
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Internal links: related Event Management listings */}
      {related.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">More Event Management in DFW</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {related.map(({ item, key }: any) => (
              <Link
                key={key}
                to={`/event-management/${key}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {item.name}
                <span className="ml-auto text-gray-400 text-[11px]">
                  {getTypes(item.type)[0] || 'Event Management'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/event-management', label: 'All Event Management in DFW' },
            { to: '/party-venues', label: 'Party Venues' },
            { to: '/performing-artists', label: 'Performing Artists' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-[12px] font-medium text-gray-600 hover:text-[#082f6d] hover:border-[#082f6d] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
